import { diffWords } from "diff";
import Delta from "quill-delta";
import { useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useDispatch } from "react-redux";

import type { DeltaStatic } from "quill";
import { Button } from "../ui/button";
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { IMitigation, MitigationType, setMitigationNewVersion } from "../../../store/playbook-analysis-slice";

interface IEditRedlineProps {
	mitigation: IMitigation;
	mitigationIdx: number;
	keyTermIdx: number;
	type: MitigationType;
}

export function EditRedline({ type, mitigation, mitigationIdx, keyTermIdx }: IEditRedlineProps) {
	const dispatch = useDispatch();

	const [quill, setQuill] = useState<ReactQuill | null>(null);
	const [isOpen, setIsOpen] = useState(false);

	function getInitialDelta(): DeltaStatic {
		const delta = new Delta();

		if (mitigation.diff) {
			mitigation.diff.forEach((text: any) => {
				const attributes: { color?: string; strike?: boolean } = {};

				if (text.removed) {
					attributes.color = "#dc2626";
					attributes.strike = true;
				} else if (text.added) {
					attributes.color = "#16a34a";
				} else {
					attributes.color = "#000000";
				}

				delta.insert(text.value + " ", attributes);
			});
		}

		return delta;
	}

	const [editorDelta, setEditorDelta] = useState(getInitialDelta());

	const modules = {
		toolbar: [[{ color: ["#dc2626", "#000000", "#16a34a"] }], ["strike"]],
	};

	const formats = ["color", "strike"];

	const handleChange = () => {
		if (quill) {
			const editor = quill.getEditor();
			const newContent = editor.getContents();
			setEditorDelta(newContent);
		}
	};

	function handleSave() {
		if (quill) {
			const editor = quill.getEditor();
			const delta = editor.getContents();

			const updatedText = delta.ops
				.filter((op) => {
					if (op.attributes?.strike) {
						return false;
					}
					return true;
				})
				.map((op) => {
					return op.insert.trim();
				})
				.join(" ");

			let updatedDiff = null;

			if (mitigation.newClause) {
				updatedDiff = diffWords("", updatedText);
			} else {
				updatedDiff = diffWords(mitigation.reference, updatedText);
			}

			dispatch(setMitigationNewVersion({ type, keyTermIdx, mitigationIdx, updatedDiff, updatedText }));
		}
		setIsOpen(false);
	}

	return (
		<Dialog open={isOpen} onOpenChange={setIsOpen}>
			<Tooltip>
				<TooltipTrigger>
					<DialogTrigger>
						<Button
							variant="ghost"
							className="size-8 bg-[#f5f5f5] hover:bg-[#e6e6e6] rounded-full grid items-center justify-center"
						>
							<svg width="19" height="20" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									d="M8.52286 4.79871C8.52286 4.79871 8.52286 5.88846 9.61261 6.97821C10.7024 8.06796 11.7921 8.06796 11.7921 8.06796M4.213 13.2496L6.50148 12.9227C6.83158 12.8755 7.13749 12.7226 7.37328 12.4868L12.8819 6.97821C13.4837 6.37636 13.4837 5.40056 12.8819 4.79871L11.7921 3.70896C11.1903 3.10711 10.2145 3.10711 9.61261 3.70896L4.10403 9.21754C3.86824 9.45333 3.71529 9.75924 3.66813 10.0893L3.3412 12.3778C3.26855 12.8864 3.70445 13.3223 4.213 13.2496Z"
									stroke="#4A88EE"
									strokeWidth="1.08333"
									strokeLinecap="round"
								/>
							</svg>
						</Button>
					</DialogTrigger>
				</TooltipTrigger>
				<TooltipContent>Edit Text</TooltipContent>
			</Tooltip>

			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>Edit Text</DialogTitle>
				</DialogHeader>
				<div>
					<ReactQuill
						theme="snow"
						value={editorDelta}
						onChange={handleChange}
						modules={modules}
						formats={formats}
						className="h-48 mb-12"
						ref={(el) => {
							setQuill(el);
						}}
					/>
				</div>
				<DialogClose asChild>
					<Button variant="outline" onClick={handleSave}>
						Save Changes
					</Button>
				</DialogClose>
			</DialogContent>
		</Dialog>
	);
}
